import { Prompt } from '@components'
import { useBusinessContext, useMobileEmbed } from '@context'
import { fetchRequisition, IBankIntegration } from '@query'
import { getRequisitionRedirectURL } from '@root/utils/getRequisitionRedirectURL'
import React from 'react'
import { useTranslation } from 'react-i18next'
import { FaSyncAlt } from 'react-icons/fa'
import { useQuery } from '@tanstack/react-query'

interface Props {
  integration: IBankIntegration
  isVisible: boolean
  onClose: () => void
}

export const RenewRequisitionPrompt: React.FC<Props> = ({ integration, isVisible, onClose }) => {
  const [t] = useTranslation()
  const { slug } = useBusinessContext()
  const {
    isEmbedded,
    redirectURL: embeddedRedirectUrl,
    errorRedirectURL: embeddedErrorRedirectUrl
  } = useMobileEmbed()

  const { redirectUrl, errorRedirectUrl } = getRequisitionRedirectURL(
    isEmbedded,
    slug,
    embeddedRedirectUrl,
    embeddedErrorRedirectUrl
  )

  const { data, isFetching, isFetched } = useQuery({
    queryKey: [
      slug,
      'requisition',
      integration.name,
      integration.type,
      integration.country,
      redirectUrl,
      errorRedirectUrl
    ],
    queryFn: () =>
      fetchRequisition({
        slug,
        aspspName: integration.name,
        aspspCountry: integration.country,
        psuType: integration.type,
        redirectUrl,
        errorRedirectUrl
      }),
    ...{ enabled: isVisible }
  })

  return (
    <Prompt
      isVisible={isVisible}
      onClose={onClose}
      title={t('settings.integrations.bank.integrationRenewTitle')}
      description={t('settings.integrations.bank.integrationRenewDescription')}
      buttons={[
        {
          text: t('general.cancel'),
          action: onClose
        },
        {
          icon: <FaSyncAlt />,
          text: t('settings.integrations.bank.integrationRenewButton'),
          link: isFetching ? undefined : data?.url,
          intent: 'primary',
          showSpinner: isFetching,
          disabled: !isFetched || isFetching
        }
      ]}
    />
  )
}
